/**
 * Phase 12 — the text ranking settings as stored (creative satellite
 * `textRankingJson`, inherited with the Texts section): defaults, a tolerant
 * parser (bad / missing pieces fall back, never throw) and the one-line
 * description the editor and the master config card both print.
 * For Media Devoted | Created By Ciro Autiero
 */
import { notEnoughDataRule, rulesWithLegacyFloor } from "../rules/default-rules";
import { parseRules } from "../rules/parse-rules";
import { DROP_GEN_TAGS_CHECK, DROP_GEN_TEXT_RULE_ACTIONS } from "../rules/rules.types";
import { defaultHourFormula } from "../hour-formula";
import {
  DROP_GEN_POLICY_KINDS,
  DROP_GEN_TEXTS_PER_DROP_MAX,
  type DropGenTextMix,
  type DropGenTextPolicy,
  describeMix,
  sameMix,
} from "./text-policy";
import { parseTextPolicy } from "./policy/policy-parse";
import {
  DROP_GEN_TEXT_RANKING_LIMITS as LIMITS,
  type DropGenTextRankingConfig,
  TEMPLATE_ID_PATTERN,
} from "./text-ranking.types";

/** The old "Min volume / text" default — the default exclude rule's number. */
export const DEFAULT_TEXT_FLOOR = 100;

/** Ranked on CR over the last 30 days, "Not enough data" under 100 unique visits; nothing changes in drafts until proven texts are asked for. */
export function defaultTextRanking(): DropGenTextRankingConfig {
  const formula = defaultHourFormula("last-30-days", "cr");
  const id = formula.groups[0].id;
  formula.groups[0] = { ...formula.groups[0], id: "texts-1", name: "Texts" };
  formula.groupWeights = { "texts-1": formula.groupWeights[id] ?? 100 };
  return { formula, provenPerDrop: 0, topN: 10, leanGroups: false, excluded: [], rules: [notEnoughDataRule(DEFAULT_TEXT_FLOOR)], policy: {} };
}

const intIn = (v: unknown, min: number, max: number, fallback: number): number =>
  typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : fallback;

/** Stored JSON → a usable config; anything malformed falls back piece by piece. */
export function parseTextRanking(json: unknown): DropGenTextRankingConfig {
  const base = defaultTextRanking();
  if (typeof json !== "object" || json === null) return base;
  const r = json as Record<string, unknown>;
  const f = r.formula as DropGenTextRankingConfig["formula"] | undefined;
  const formula = f && Array.isArray(f.groups) && f.groups.length > 0 ? { groups: f.groups, groupWeights: f.groupWeights ?? {} } : base.formula;
  const excluded = Array.isArray(r.excluded)
    ? [...new Set(r.excluded.filter((x): x is string => typeof x === "string" && TEMPLATE_ID_PATTERN.test(x)))].slice(0, LIMITS.excludedMax)
    : [];
  return {
    formula,
    rules: parseRules(rulesWithLegacyFloor(r, "minVolume", DEFAULT_TEXT_FLOOR), DROP_GEN_TEXT_RULE_ACTIONS, DROP_GEN_TAGS_CHECK),
    provenPerDrop: intIn(r.provenPerDrop, 0, DROP_GEN_TEXTS_PER_DROP_MAX, base.provenPerDrop),
    topN: intIn(r.topN, 1, LIMITS.topNMax, base.topN),
    leanGroups: r.leanGroups === true,
    excluded,
    policy: parseTextPolicy(r.policy),
  };
}

/** "5 proven + 10 AI texts per drop (top 10) · 5 kinds of drops proven only · fresh AI texts lean on the best prompt groups". */
export function describeTextRankingAction(
  c: Pick<DropGenTextRankingConfig, "provenPerDrop" | "topN" | "leanGroups"> & { policy?: DropGenTextPolicy },
  /** The Generation step's AI texts per drop. */
  aiPerDrop: number,
): string {
  const ordinary: DropGenTextMix = { proven: c.provenPerDrop, ai: aiPerDrop };
  const reuse = c.provenPerDrop > 0 ? `${describeMix(ordinary)} texts per drop (top ${c.topN})` : "drafts keep fresh AI texts";
  const lean = c.leanGroups && aiPerDrop > 0 ? " · fresh AI texts lean on the best prompt groups" : "";
  return `${reuse}${describePolicy(c.policy, ordinary)}${lean}`;
}

/** Phase 37 — " · 5 kinds of drops proven only" when a kind differs from the rest. */
function describePolicy(policy: DropGenTextPolicy | undefined, ordinary: DropGenTextMix): string {
  const apart = DROP_GEN_POLICY_KINDS.filter((k) => policy?.[k] !== undefined && !sameMix(policy[k]!, ordinary));
  if (apart.length === 0) return "";
  const n = `${apart.length} kind${apart.length > 1 ? "s" : ""} of drops`;
  return apart.every((k) => policy![k]!.proven > 0 && policy![k]!.ai === 0) ? ` · ${n} proven only` : ` · ${n} with a different mix`;
}
